/**
 * License React Query Hooks
 * 라이센스 관리 React Query 훅
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import {
	getLicensesByOrganization,
	createLicense,
	updateLicense,
	deleteLicense,
	UpdateLicenseRequest,
} from '../services/licenseService';
import { getOrganizationDeviceList, getOrganizationDeviceAllocation } from '../services/deviceService';
import type { GetDeviceListResponseDto, GetOrganizationDeviceAllocationResultDto } from '../types/api/device';
import { useApiLanguage } from '@/hooks/useApiLanguage';

// Query Keys
export const licenseKeys = {
	all: ['licenses'] as const,
	lists: () => [...licenseKeys.all, 'list'] as const,
	byOrganization: (organizationId: string) => [...licenseKeys.lists(), 'organization', organizationId] as const,
};

/**
 * 조직의 라이센스 목록 조회
 */
export const useLicensesByOrganization = (
	organizationId: string,
	options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) => {
	return useQuery({
		queryKey: licenseKeys.byOrganization(organizationId),
		queryFn: () => getLicensesByOrganization(parseInt(organizationId)),
		enabled: !!organizationId,
		staleTime: 3 * 60 * 1000,
		...options,
	});
};

/**
 * 라이센스 생성
 */
export const useCreateLicense = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationId,
			licenseData
		}: {
			organizationId: string;
			licenseData: any
		}) => createLicense({ ...licenseData, organizationId: parseInt(organizationId) }),
		onSuccess: (_result, variables) => {
			queryClient.invalidateQueries({
				queryKey: licenseKeys.byOrganization(variables.organizationId)
			});
		},
	});
};

/**
 * 라이센스 업데이트
 */
export const useUpdateLicense = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			_organizationId,
			licenseId,
			licenseData,
		}: {
			_organizationId: string;
			licenseId: string;
			licenseData: UpdateLicenseRequest;
		}) => updateLicense(parseInt(licenseId), licenseData as any),
		onSuccess: (_result, variables) => {
			queryClient.invalidateQueries({
				queryKey: licenseKeys.byOrganization(variables._organizationId)
			});
		},
	});
};

/**
 * 라이센스 삭제
 */
export const useDeleteLicense = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			_organizationId,
			licenseId,
		}: {
			_organizationId: string;
			licenseId: string;
		}) => deleteLicense(parseInt(licenseId)),
		onSuccess: (_result, variables) => {
			// 해당 조직의 라이센스 목록 캐시 무효화
			queryClient.invalidateQueries({
				queryKey: licenseKeys.byOrganization(variables._organizationId)
			});
		},
	});
};

/**
 * 기관 디바이스 목록 조회
 * GET /device/organization/list?organizationId=...
 */
export const useOrganizationDeviceList = (
	organizationId: number | string | undefined,
	options?: Omit<UseQueryOptions<GetDeviceListResponseDto>, 'queryKey' | 'queryFn'>
) => {
	const id = organizationId != null ? Number(organizationId) : undefined;
	return useQuery({
		queryKey: ['device', 'org', id, 'list'],
		queryFn: () => getOrganizationDeviceList(id!),
		enabled: !!id,
		staleTime: 3 * 60 * 1000,
		...options,
	});
};

/**
 * 기관 클래스—기기 할당 현황
 * GET /organization/{organizationId}/device-allocation
 */
export const useOrganizationDeviceAllocation = (
	organizationId: number | string | undefined,
	options?: Omit<UseQueryOptions<GetOrganizationDeviceAllocationResultDto>, 'queryKey' | 'queryFn'>
) => {
	const id = organizationId != null ? Number(organizationId) : undefined;
	const language = useApiLanguage();
	const { enabled: enabledOpt, ...rest } = (options ?? {}) as typeof options & {
		enabled?: boolean;
	};
	return useQuery({
		queryKey: ['device', 'org', id, 'device-allocation', language],
		queryFn: () => getOrganizationDeviceAllocation(id!, { language }),
		enabled: id != null && id > 0 && (enabledOpt === undefined || enabledOpt),
		staleTime: 0,
		...rest,
	});
};
